/**
 * Object model of a FastReport 4 template (.fr3).
 *
 * JavaScript port of the tablet's Fr3Doc (MyFasManMobile
 * library/services/report/fr3). The two engines are kept one to one and are
 * checked against the same golden fixtures (MyFasManMobile
 * tools/fr3harness/golden): a change on either side has to be made, and
 * verified, on both.
 *
 * Geometry is kept in native fr3 units (1/96 inch) and only converted to PDF
 * points at emit time; see UNITS_TO_POINTS.
 */

/** fr3 units are 1/96 inch, PDF points are 1/72 inch. Exact, no rounding. */
export const UNITS_TO_POINTS = 0.75;

export const MM_TO_POINTS = 72 / 25.4;

export const BandKind = Object.freeze({
    PAGE_HEADER: 'PAGE_HEADER',
    GROUP_HEADER: 'GROUP_HEADER',
    MASTER_DATA: 'MASTER_DATA',
    GROUP_FOOTER: 'GROUP_FOOTER',
    REPORT_SUMMARY: 'REPORT_SUMMARY',
    PAGE_FOOTER: 'PAGE_FOOTER',
    REPORT_TITLE: 'REPORT_TITLE',
    HEADER: 'HEADER',
    FOOTER: 'FOOTER',
    CHILD: 'CHILD',
    DETAIL_DATA: 'DETAIL_DATA',
    COLUMN_HEADER: 'COLUMN_HEADER',
    COLUMN_FOOTER: 'COLUMN_FOOTER',
    OVERLAY: 'OVERLAY',
    UNKNOWN: 'UNKNOWN',
});

const BAND_TAGS = {
    TfrxPageHeader: BandKind.PAGE_HEADER,
    TfrxGroupHeader: BandKind.GROUP_HEADER,
    TfrxMasterData: BandKind.MASTER_DATA,
    TfrxGroupFooter: BandKind.GROUP_FOOTER,
    TfrxReportSummary: BandKind.REPORT_SUMMARY,
    TfrxPageFooter: BandKind.PAGE_FOOTER,
    TfrxReportTitle: BandKind.REPORT_TITLE,
    TfrxHeader: BandKind.HEADER,
    TfrxFooter: BandKind.FOOTER,
    TfrxChild: BandKind.CHILD,
    TfrxDetailData: BandKind.DETAIL_DATA,
    TfrxColumnHeader: BandKind.COLUMN_HEADER,
    TfrxColumnFooter: BandKind.COLUMN_FOOTER,
    TfrxOverlay: BandKind.OVERLAY,
};

export const bandKindOf = (tag) => (tag && Object.prototype.hasOwnProperty.call(BAND_TAGS, tag)
    ? BAND_TAGS[tag] : BandKind.UNKNOWN);

export const ObjKind = Object.freeze({
    MEMO: 'MEMO', PICTURE: 'PICTURE', BARCODE: 'BARCODE', LINE: 'LINE', SHAPE: 'SHAPE', UNKNOWN: 'UNKNOWN',
});

export const HAlign = Object.freeze({ LEFT: 'LEFT', RIGHT: 'RIGHT', CENTER: 'CENTER', BLOCK: 'BLOCK' });

export const VAlign = Object.freeze({ TOP: 'TOP', BOTTOM: 'BOTTOM', CENTER: 'CENTER' });

/** TfrxFrameTypes set bits, as emitted by Delphi. */
export const FRAME_LEFT = 1;
export const FRAME_RIGHT = 2;
export const FRAME_TOP = 4;
export const FRAME_BOTTOM = 8;

export const newDoc = () => ({
    version: '',
    /** Kept after an inherited template was merged, for diagnostics only. */
    parentReport: '',
    scriptText: '',
    printCopies: 1,
    pages: [],
    /** Report variables (design-time expressions); the caller's Options.variables win. */
    variables: new Map(),
    /** Parse-time notes: bands and objects the engine does not draw. */
    warnings: [],
    /** Set by the parser when an <inherited> template was parsed without its parent. */
    unmergedParent: false,
});

export const firstPage = (doc) => (doc.pages.length === 0 ? null : doc.pages[0]);

/** True only for a template whose parent could not be merged. */
export const isInherited = (doc) => doc.unmergedParent;

export const newPage = () => ({
    name: '',
    paperWidthMm: 210,
    paperHeightMm: 297,
    leftMarginMm: 0,
    rightMarginMm: 0,
    topMarginMm: 0,
    bottomMarginMm: 0,
    bands: [],
});

export const pageWidthPoints = (p) => p.paperWidthMm * MM_TO_POINTS;
export const pageHeightPoints = (p) => p.paperHeightMm * MM_TO_POINTS;

export const newBand = () => ({
    kind: BandKind.UNKNOWN,
    name: '',
    rawTag: '',
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    condition: '',
    onBeforePrint: '',
    dataSetName: '',
    /** Name of the TfrxChild band printed right after this one, or "". */
    childName: '',
    /** Band.Stretched: grows with its stretching memos. */
    stretched: false,
    visible: true,
    /** Header / GroupHeader: printed again at the top of every new page. */
    reprintOnNewPage: false,
    startNewPage: false,
    objects: [],
});

export const newObj = () => ({
    kind: ObjKind.UNKNOWN,
    name: '',
    // Design-time geometry, in fr3 units, relative to the owning band.
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    text: '',
    dataField: '',
    dataSetName: '',
    fontName: 'Arial',
    /** Delphi stores this negative, in pixels at 96 dpi. */
    fontHeight: -13,
    fontStyle: 0,
    fontColor: 0x000000,
    frameTyp: 0,
    frameColor: 0x000000,
    frameWidth: 1,
    /** Background fill; null means clNone (transparent). */
    color: null,
    hAlign: HAlign.LEFT,
    vAlign: VAlign.TOP,
    formatStr: '',
    formatKind: '',
    decimalSeparator: '',
    hideZeros: false,
    visible: true,
    wordWrap: true,
    /** "", "smActualHeight" or "smMaxHeight" (FastReport TfrxStretchMode). */
    stretchMode: '',
    allowHtmlTags: false,
    // Picture
    center: false,
    transparent: false,
    /** Raw graphic (BMP/PNG/JPEG/GIF) with the Delphi stream prefix already stripped. */
    embeddedImage: null,
    // Barcode
    /** FastReport TfrxBarcodeType, e.g. "bcCodeEAN13". */
    barType: '',
    expression: '',
    showText: true,
    calcCheckSum: true,
    rotation: 0,
    zoom: 1,
    // Line / Shape
    diagonal: false,
    /** TfrxShapeKind, e.g. "skRectangle". */
    shape: '',
});

export const fontSizePoints = (o) => {
    let px = Math.abs(o.fontHeight);
    if (px <= 0) px = 13;
    return px * UNITS_TO_POINTS;
};

// (fontStyle & n) on an int, as Java does after the (int) cast.
export const isBold = (o) => (o.fontStyle & 1) !== 0;
export const isItalic = (o) => (o.fontStyle & 2) !== 0;
export const isUnderline = (o) => (o.fontStyle & 4) !== 0;
